import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workforceRequestApi } from '../../api/workforce-request';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Eye, Send, Check, X, PlusCircle } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { ReturnAction } from '../../components/ReturnAction';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', submitted: 'Pending Approval', returned: 'Returned for Redraft', approved: 'Approved', rejected: 'Rejected' };

export function WorkforceRequestsPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const requestsQuery = useQuery({ queryKey: ['workforce-requests'], queryFn: () => workforceRequestApi.list() });
  const { register, handleSubmit, reset } = useForm<Record<string, string>>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['workforce-requests'] });

  const createMutation = useMutation({
    mutationFn: (values: Record<string, string>) => workforceRequestApi.create({
      requestType: values.requestType, reason: values.reason, numberOfPositions: Number(values.numberOfPositions) || 1,
      employmentType: values.employmentType || undefined, grade: values.grade || undefined, proposedStartDate: values.proposedStartDate || undefined,
    }),
    onSuccess: (result) => { showSuccess(`Request created - ${result.data.requestReference}`); invalidate(); setModalOpen(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create request.'),
  });

  const submitMutation = useMutation({
    mutationFn: (id: number) => workforceRequestApi.submit(id),
    onSuccess: () => { showSuccess('Submitted for approval.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit.'),
  });

  const decideMutation = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) => workforceRequestApi.decide(id, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <PageHeader
        title="Workforce Requests"
        description="A position must be requested and approved here before a vacancy can be created from it."
        actions={<button className="btn-primary" onClick={() => setModalOpen(true)}>New Request</button>}
      />

      {requestsQuery.data && (
        <div className="panel">
          <SectionHeader title="All Workforce Requests" />
          <div className="panel-body">
        <DataTable
          rows={requestsQuery.data.data}
          rowKey={(r) => r.id}
          emptyMessage="No workforce requests yet."
          columns={[
            { header: 'Reference', render: (r) => r.request_reference },
            { header: 'Position', render: (r) => r.position_title ?? '-' },
            { header: 'Department', render: (r) => r.department_name ?? '-' },
            { header: 'Type', render: (r) => r.request_type === 'replacement' ? 'Replacement' : 'New Position' },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
            {
              header: '',
              render: (r) => (
                <span className="icon-action-group">
                  <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/hr/workforce-requests/${r.id}`)} />
                  {r.status === 'draft' && <IconActionButton icon={Send} label="Submit" onClick={() => submitMutation.mutate(r.id)} />}
                  {r.status === 'submitted' && (
                    <>
                      <IconActionButton icon={Check} label="Approve" onClick={() => decideMutation.mutate({ id: r.id, decision: 'approved' })} />
                      <IconActionButton icon={X} label="Reject" danger onClick={() => decideMutation.mutate({ id: r.id, decision: 'rejected' })} />
                      <ReturnAction onReturn={(comment) => workforceRequestApi.return(r.id, comment)} queryKeysToInvalidate={[['workforce-requests']]} />
                    </>
                  )}
                  {r.status === 'approved' && (
                    <Link className="icon-action" to={`/hr/vacancies?fromRequest=${r.id}`} aria-label="Create Vacancy">
                      <span className="icon-action-tile"><PlusCircle size={20} /></span>
                      <span className="icon-action-label">Create Vacancy</span>
                    </Link>
                  )}
                </span>
              ),
            },
          ]}
        />
        {requestsQuery.data.data.filter((r) => r.status === 'returned').map((r) => (
          <ReturnedBanner key={r.id} returnComment={r.return_comment} resubmit={() => workforceRequestApi.resubmit(r.id)} queryKeysToInvalidate={[['workforce-requests']]} />
        ))}
          </div>
        </div>
      )}

      {modalOpen && (
        <Modal title="New Workforce Request" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Request Type
              <select {...register('requestType')}>
                <option value="new_position">New Position</option>
                <option value="replacement">Replacement</option>
              </select>
            </label>
            <label>Number of Positions<input type="number" defaultValue={1} {...register('numberOfPositions')} /></label>
            <label>Grade<input {...register('grade')} /></label>
            <label>
              Employment Type
              <select {...register('employmentType')}>
                <option value="">- not set -</option>
                <option value="full_time">Full Time</option><option value="part_time">Part Time</option>
                <option value="consultant">Consultant</option><option value="intern">Intern</option><option value="volunteer">Volunteer</option>
              </select>
            </label>
            <label>Proposed Start Date<input type="date" {...register('proposedStartDate')} /></label>
            <label>Reason / Justification<textarea rows={3} {...register('reason')} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>Create</button><button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
